import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import { QueryClient, QueryClientProvider } from "react-query";
import { SnackbarProvider } from "notistack";

import Layout from "./components/Layout";
import Dashboard from "./pages/Dashboard";
import EshopManagement from "./pages/EshopManagement";
import JobManagement from "./pages/JobManagement";
import Monitoring from "./pages/Monitoring";
import ProductManagement from "./pages/ProductManagement";
import UsersPage from "./pages/UsersPage";
import Login from "./pages/Login";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ScraperProvider } from "./contexts/ScraperContext";
import ScraperMonitoringDashboard from "./components/ScraperMonitoringDashboard";
import EshopWizardPage from "./pages/EshopWizardPage";
import ScraperTestPage from "./pages/ScraperTestPage";
import Settings from "./pages/Settings";
import { ThemeProvider } from "./contexts/ThemeContext";


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return null; // Or a loading spinner
  }

  return isAuthenticated ? children : <Navigate to="/login" />;
}

function AppRoutes() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={!isAuthenticated ? <Login /> : <Navigate to="/" />}
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout>
              <Dashboard />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/eshops"
        element={
          <ProtectedRoute>
            <Layout>
              <EshopManagement />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/jobs"
        element={
          <ProtectedRoute>
            <Layout>
              <JobManagement />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/monitoring"
        element={
          <ProtectedRoute>
            <Layout>
              <Monitoring />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/products"
        element={
          <ProtectedRoute>
            <Layout>
              <ProductManagement />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/scraper-monitoring"
        element={
          <ProtectedRoute>
            <Layout>
              <ScraperMonitoringDashboard />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/eshop-wizard"
        element={
          <ProtectedRoute>
            <Layout>
              <EshopWizardPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/eshop-wizard/:eshopId"
        element={
          <ProtectedRoute>
            <Layout>
              <EshopWizardPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/scraper-test"
        element={
          <ProtectedRoute>
            <Layout>
              <ScraperTestPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Layout>
              <Settings />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute>
            <Layout>
              <UsersPage />
            </Layout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <SnackbarProvider maxSnack={3}>
          <Router>
            <AuthProvider>
              <ScraperProvider>
                <AppRoutes />
              </ScraperProvider>
            </AuthProvider>
          </Router>
        </SnackbarProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
